import { AfterViewInit, Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { NavigationEnd, Router, Scroll } from '@angular/router';

import { MenuItem, MessageService,  } from 'primeng/api';
import { Observable, Subscription, filter, mergeMap, tap } from 'rxjs';
import { cloneDeep } from 'lodash';

import { TableColumnModel } from '../../../Common/Forms/models/table-column.model';
import { TableColumnInputEnum } from '../../../Common/Forms/enums/table-column-input-type.enum';
import { MfaModel, MfaTypesEnum } from '../../../Core/Users/models/mfa.model';
import { MfaService } from '../../services/mfa.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { TextInputDialogModel } from '../../../Common/Dialogs/models/text-input-dialog.model';
import { UserModel } from '../../../Core/Users/models/user.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { TextInputDialogComponent } from '../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { UsersApi } from '../../../api/users/users.api';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';

import { MfaSetupComponent } from './mfa-setup.component';



interface MfaDataRow  {
    id?: number;
    type?: string;
    originalData?: MfaModel;
    identifier?: string;
    actions?: MenuItem[];
}

@Component({
    selector: 'agpr-mfa-management',
    styleUrls: ['./mfa-management.component.scss'],
    templateUrl: './mfa-management.component.html',
    standalone: false
})
export class MfaManagementComponent implements AfterViewInit {
    @Input() addNewMethod: boolean = false;
    @ViewChild('actionCell') public actionCellTemplate: TemplateRef<any>;
    @ViewChild('methodNameCell') public methodNameCellTemplate: TemplateRef<any>;

    readonly buttonStyles = ButtonTypesEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly mfaTypes = MfaTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    setAsPreferredAction: MenuItem = {
        label: this.localizationService.transform('Mfa.setAsPreferred'),
        command: (event: any) => {
            this.performAction(
                this.mfaService.setPreferredMethod(cloneDeep((event.data as MfaDataRow).originalData)).pipe(
                    tap({
                        next: () => {
                            this.messageService.add({
                                severity: messageSeverityEnum.success,
                                summary: 'Mfa.preferred.success',
                            });
                        },
                        error: () => {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Mfa.preferred.error',
                            });
                        }
                    })
                )
            );
            event?.originalEvent?.stopImmediatePropagation();
        }
    };
    deleteAction: MenuItem = {
        label: this.localizationService.transform('Mfa.delete'),
        command: (event: any) => {
            const rowToDelete = (event.data as MfaDataRow).originalData;
            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    heading: this.localizationService.transform('Mfa.management.delete.heading'),
                    alertType: messageSeverityEnum.warning,
                    okLabel: this.localizationService.transform('Mfa.delete'),
                    alertMessage: this.localizationService.transform('Mfa.management.delete.warningMessage'),
                    message: this.localizationService.transform('Mfa.management.delete.message'),
                    list: [this.localizationService.transform(`Mfa.method.${rowToDelete.type}`,
                        {name: rowToDelete.type === MfaTypesEnum.email ? rowToDelete.email : rowToDelete.name}
                    )]
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.performAction(
                                this.mfaService.deleteMethod(rowToDelete).pipe(
                                    tap({
                                        next: () => {
                                            this.messageService.add({
                                                severity: messageSeverityEnum.success,
                                                summary: 'Mfa.delete.success',
                                            });
                                        },
                                        error: () => {
                                            this.messageService.add({
                                                severity: messageSeverityEnum.error,
                                                summary: 'Mfa.delete.error',
                                            });
                                        }
                                    })
                                )
                            );
                            event?.originalEvent?.stopImmediatePropagation();
                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
        }
    };

    columnDefs: TableColumnModel[] = [];
    dataRows: MfaDataRow[] = [];
    isValidated: boolean = false;
    currentUser: UserModel;
    subscriptions: Subscription[] = [];

    constructor(
        private usersApi: UsersApi,
        private localizationService: LocalizationService,
        public mfaService: MfaService,
        private messageService: MessageService,
        private coreDialogService: CoreDialogService,
        private currentUserStateService: CurrentUserStateService,
        private router: Router

    ) {
        this.subscriptions.push(
            this.mfaService.all$.pipe(
                mergeMap(all => {
                    const dataRows: MfaDataRow[] = [];
                    all?.sort((x1, x2) => {
                        if (x1.preferred) {
                            return -1;
                        }
                        return 1;
                    });
                    all?.forEach(x => {
                        dataRows.push({
                            id: x.id,
                            type: this.localizationService.transform(`Mfa.${x.type}`),
                            identifier: x.type === MfaTypesEnum.email ? x.email : x.name,
                            originalData: x,
                            actions: all.length > 1 ? [this.setAsPreferredAction] : []
                        });
                    });
                    return this.currentUserStateService.getAccount().pipe(
                        tap(account => {
                            if (account) {
                                if ((account.isMfaRequired && dataRows.length > 1) || !account.isMfaRequired) {
                                    dataRows.forEach(x => x.actions.push(this.deleteAction));
                                }
                            }
                            this.dataRows = dataRows;

                        })
                    );
                })
            ).subscribe(),
            this.router.events.subscribe(e => {
                let matches;
                const urlParse: RegExp = /AccountManagement/;
                if (e instanceof NavigationEnd) {
                    matches = e.urlAfterRedirects.match(urlParse);
                }
                if (e instanceof Scroll) {
                    if (e.routerEvent instanceof NavigationEnd) {
                        matches = e.routerEvent.urlAfterRedirects.match(urlParse);
                    }
                }
                // always set to true when in admin so users don't need to confirm password before modify MFA
                if (matches) {
                    this.isValidated = true;
                }
            }),
            this.currentUserStateService.getUser().pipe(filter(x => !!x)).subscribe(user => this.currentUser = user),
        );

    }

    ngAfterViewInit(): void {
        this.subscriptions.push(
            this.currentUserStateService.getAccount().subscribe(account => {
                if (this.addNewMethod && account) {
                    this.addNewMethod = account?.enabledMfaTypes?.length > 0;
                }
            })
        );

        requestAnimationFrame(() => {
            this.columnDefs = [
                {
                    type: TableColumnInputEnum.custom,
                    heading: this.localizationService.transform('Mfa.management.method'),
                    field: 'type',
                    sortable: false,
                    customTemplate: this.methodNameCellTemplate
                },
                {
                    heading: this.localizationService.transform('Mfa.management.identifier'),
                    field: 'identifier',
                    sortable: false,
                    width: 300
                },
                {
                    type: TableColumnInputEnum.custom,
                    heading: this.localizationService.transform('Mfa.management.action'),
                    sortable: false,
                    customTemplate: this.actionCellTemplate
                }
            ];
        });
    }


    createNew() {
        const createNewAction = this.mfaService.createNewMfaMethod().pipe(tap(x => {
            this.coreDialogService.open(MfaSetupComponent, {
                inputs: {
                    showAsDialog: true,
                    confirm: (mfaModel: MfaModel) => {
                        const verifyModel = mfaModel.toMfaVerifyModel();
                        return this.usersApi.verifyMfa(verifyModel).pipe(
                            tap({
                                next: () => {
                                    this.messageService.add({
                                        severity: messageSeverityEnum.success,
                                        summary: `Mfa.setup.success.${mfaModel.type}`,
                                    });
                                    this.mfaService.refreshAllMfaSetting();
                                }
                            })
                        );
                    },
                },
                outputs: {
                    cancel: () => {
                        this.coreDialogService.open(ConfirmDialogComponent, {
                            inputs: {
                                heading: 'Mfa.management.createNew.cancelHeading',
                                message: 'Mfa.management.createNew.cancelMessage',
                                okLabel: 'Mfa.confirm'
                            },
                            outputs: {
                                dialogClose: (confirmed: boolean) => {
                                    if (confirmed) {
                                        this.coreDialogService.close('MfaSetupDialog');
                                    }
                                }
                            }
                        });
                    }
                }
            });
        }));

        this.performAction(createNewAction);
    }


    private performAction(actionObservable: Observable<any>) {
        if (!this.isValidated) {
            const confirmPasswordDialog: TextInputDialogModel = {
                inputs: {
                    id: 'mfaManagement.confirmPasswordDialog',
                    heading: 'Mfa.confirm.password',
                    message: 'Mfa.confirm.password.message',
                    description: 'Mfa.password',
                    type: 'password',
                    width: 550,
                    okLabel: 'Mfa.confirm',
                    submit: (password: string) => {
                        return this.usersApi.validatePassword(this.currentUser.username, password).pipe(
                            tap({
                                error: () => {
                                    this.isValidated = false;
                                    this.messageService.add({
                                        summary:  'Mfa.confirm.password.error',
                                        severity: messageSeverityEnum.error
                                    });
                                },
                                complete: () => {
                                    this.isValidated = true;
                                    actionObservable.subscribe(() => this.mfaService.refreshAllMfaSetting());
                                }
                            })
                        );
                    }
                }
            };
            this.coreDialogService.open(TextInputDialogComponent, confirmPasswordDialog);
        } else {
            actionObservable.subscribe(() => this.mfaService.refreshAllMfaSetting());
        }
    }
}
